import { ExtendedObjectsDocument, useLessonQuery, useLessonsQuery, useObjectQuery, useLessonsOverviewQuery, LessonsOverviewDocument, } from 'api/generated';
import { getBaseOptions } from 'api/wrappers';
import { formatDateTimePeriod, getApiStringFromDate, getDateFromApiString, } from 'utils/date';
import { getFileIcon } from 'utils/file-icons';
export const useLessonsQueryApi = (variables, options) => {
    const { data, ...rest } = useLessonsQuery(getBaseOptions(typeof variables === 'object'
        ? {
            from: getApiStringFromDate(variables.from),
            to: getApiStringFromDate(variables.to),
        }
        : variables, options));
    const lessons = (data?.lessons ?? [])
        .map((lesson) => ({
        ...lesson,
        endDate: getDateFromApiString(lesson.endDateTime),
        startDate: getDateFromApiString(lesson.startDateTime),
    }))
        .sort((a, b) => {
        const compareName = a.course.name.localeCompare(b.course.name);
        if (compareName) {
            return compareName;
        }
        return a.id < b.id ? -1 : 1;
    });
    return { ...rest, data: lessons };
};
export const useObjectLessonsQueryApi = (variables, options) => {
    const { data, ...rest } = useObjectQuery(getBaseOptions(variables, options));
    if (!data?.object) {
        return { ...rest, data: undefined };
    }
    const lessons = data.object.lessons.map((lesson) => ({
        ...lesson,
        endDate: getDateFromApiString(lesson.endDateTime),
        startDate: getDateFromApiString(lesson.startDateTime),
    }));
    const object = {
        id: data.object.id,
        mimeType: data.object.mimeType,
        name: data.object.currentVersion.name,
        lessons,
    };
    return { ...rest, data: object };
};
export const useLessonQueryApi = (variables, options) => {
    const { data, ...rest } = useLessonQuery(getBaseOptions(variables, options));
    if (data?.lesson) {
        const { lesson } = data;
        return {
            ...rest,
            data: {
                courseColor: lesson.course.color,
                courseId: lesson.course.id,
                courseName: lesson.course.name,
                educationalYearName: lesson.course.educationalYear.name,
                endDate: getDateFromApiString(lesson.endDateTime),
                homework: lesson.homework,
                id: lesson.id,
                nextLessonId: lesson.nextLesson?.id,
                notes: lesson.notes,
                previousHomework: lesson.previousLesson?.homework ?? '',
                previousLessonId: lesson.previousLesson?.id,
                sharingEndDateTime: lesson.sharingEndDateTime
                    ? getDateFromApiString(lesson.sharingEndDateTime)
                    : undefined,
                sharingToken: lesson.sharingToken,
                schoolYearName: lesson.course.schoolYear.name,
                startDate: getDateFromApiString(lesson.startDateTime),
                subjectName: lesson.course.subject.name,
            },
        };
    }
    return { ...rest, data: undefined };
};
export const getLessonMutationOptions = (lesson) => ({
    variables: {
        input: lesson,
    },
});
export { useUpdateLessonMutation } from 'api/generated';
export const getRemoveObjectsFromLessonMutationOptions = (lessonId, objectIds) => ({
    variables: {
        lessonId,
        objectIds,
    },
    refetchQueries: [ExtendedObjectsDocument, LessonsOverviewDocument],
});
export { useRemoveObjectsFromLessonMutation } from 'api/generated';
export const getAddObjectsToLessonMutationOptions = (lessonId, objectIds) => ({
    variables: {
        lessonId,
        objectIds,
    },
    refetchQueries: [ExtendedObjectsDocument, LessonsOverviewDocument],
});
export { useAddObjectsToLessonMutation } from 'api/generated';
export const getUpdateObjectLessonsMutationOptions = (objectId, lessonIds) => {
    return {
        variables: {
            input: {
                id: objectId,
                lessonIds,
            },
        },
        refetchQueries: [ExtendedObjectsDocument],
    };
};
export { useUpdateObjectLessonsMutation } from 'api/generated';
export const getMoveObjectsToAnotherLessonMutationOptions = (oldLessonId, newLessonId, objects) => {
    objects = Array.isArray(objects) ? objects : [objects];
    const objectIds = objects.map((obj) => obj.id);
    return {
        variables: {
            oldLessonId,
            newLessonId,
            objectIds,
        },
        refetchQueries: [ExtendedObjectsDocument],
    };
};
export const useLessonsOverviewQueryApi = (variables, options) => {
    const { data, ...rest } = useLessonsOverviewQuery(getBaseOptions(variables, options));
    if (!data) {
        return { data, ...rest };
    }
    const lessons = data.lessonOverview.lessons
        .map((lesson) => ({
        ...lesson,
        formatedDateTimePeriod: formatDateTimePeriod(new Date(lesson.startDateTime), new Date(lesson.endDateTime)),
        notesFirstLine: lesson.notes.split('\n', 1)[0],
        objects: lesson.objects
            .map((obj) => {
            return {
                id: obj.id,
                Icon: getFileIcon(obj.mimeType),
                name: obj.currentVersion.name,
            };
        })
            .sort((a, b) => a.name.localeCompare(b.name)),
    }))
        .sort((a, b) => a.startDateTime.localeCompare(b.startDateTime));
    return { ...rest, data: { ...data.lessonOverview, lessons } };
};
